import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useState, useMemo, useCallback } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import * as Sentry from '@sentry/nextjs';
import BadRequestApiError from '@/interfaces/errors/badRequest';
import UnauthorizedApiError from '@/interfaces/errors/unauthorized';
import PaymentReqApiError from '@/interfaces/errors/paymentRequired';
import ForbiddenApiError from '@/interfaces/errors/forbidden';
import { deleteNullValues } from '@/utils/objects';
import widgetUtils from '@/utils/widget';
import { widgetToHash } from '@/utils/widget/hashing';
import { selectEditingArea } from '@/components/Editor/hooks/useEditingAreas';
import logger from '@/utils/logger';
import widgetsApi from '@/services/api/widgets';
import teamsApi from '@/services/api/teams';
import { nowUnix } from '@/utils/date';
import * as errors from './errors';
const EDITABLE_ATTRS = ['elementId', 'title', 'description', 'config', 'layout', 'sections'];
const PREFIX = 'features: WidgetEdit: hooks: useWidgetUpdate:';
export default function useWidgetUpdate(remoteState, workbook) {
  let formats = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  let sendNotification = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : () => undefined;
  if (!remoteState.id) throw Error(`${PREFIX} widget requires id`); // const pristineState = cloneEditableWidget(remoteState);

  const {
    0: payload,
    1: setPayload
  } = useState(cloneEditableWidget(remoteState));
  const {
    0: isLoading,
    1: setIsLoading
  } = useState(false);
  const remoteStateHash = useMemo(() => widgetToHash(remoteState), [remoteState]);
  const {
    0: updatedAt,
    1: setUpdatedAt
  } = useState(remoteState.updatedAt);
  const {
    0: hasUpdates,
    1: setHasUpdates
  } = useState(false);
  const {
    0: formFieldsets,
    1: setFormFieldsets
  } = useState(countWidgetFieldsets(payload));
  const {
    0: hasAcknowledgedInactiveSubscription,
    1: setHasAcknowledgedInactiveSubscription
  } = useState(true);

  const acknowledgeInactiveSubscription = () => {
    setHasAcknowledgedInactiveSubscription(true);
  };

  const handleChange = (target, update) => {
    try {
      const {
        result: updateResult,
        meta
      } = widgetUtils.update({
        target,
        update,
        localState: payload,
        workbook,
        formats
      });
      Object.assign(payload, updateResult);
      setPayload(payload);
      setFormFieldsets(countWidgetFieldsets(updateResult));
      setUpdatedAt(Date.now());
      setHasUpdates(widgetToHash(payload) !== remoteStateHash); // Handle widget update side-effects here

      handleUpdateSideEffects({
        widgetId: remoteState.id || '',
        meta
      });
    } catch (err) {
      const error = Error(`${PREFIX} handleChange: ${err}`); // TODO move to controller

      sendNotification({
        description: 'Update failed unexpectedly, please contact support via the chat',
        type: 'error'
      });
      Sentry.captureException(error);
      logger.error(err);
    }
  }; // Add an image file to a teams workspace


  const handleUpload = async (target, srcUpdate, file) => {
    setIsLoading(true); // Upload file to team's content

    let downloadUrl = '';

    try {
      downloadUrl = await teamsApi.createUpload(file);
    } catch (err) {
      const error = Error(`${PREFIX} handleUpload: ${err}`);
      sendNotification({
        description: 'Upload failed unexpectedly, please contact support via the chat',
        type: 'error'
      });
      Sentry.captureException(error);
      setIsLoading(false); // TODO handle 400 user errors API

      return;
    } // Interpolate download URL into updates


    const update = Object.keys(srcUpdate).reduce((acc, attr) => {
      if (typeof acc[attr] === 'string') {
        acc[attr] = acc[attr].replace(/{{downloadUrl}}/g, downloadUrl);
      }

      return acc;
    }, cloneDeep(srcUpdate)); // Add changes to payload

    handleChange(target, update); // Publist immediately before
    // download URL gets lost

    await save({
      widget: payload
    }); // Sanity check

    setIsLoading(false);
  }; // Update state after widget
  // successfully created or updated


  const handleSuccessUpsertResponse = useCallback(updatedWidget => {
    // Update payload with latest state
    Object.assign(payload, updatedWidget);
    setPayload(payload); // Don't allow additional submits
    // until more changes are made

    setHasUpdates(false);
  }, [payload, setPayload, setHasUpdates]); // Create a new widget or update an existing widget

  const save = useCallback(config => {
    let request;
    const reqType = config.isCreating ? 'POST' : 'PUT';
    setIsLoading(true);

    if (config.isCreating) {
      request = widgetsApi.createRecord(createNewWidgetPayload(config.widget, config.workbookId, config.projectId));
    } else {
      const isPublishing = config.isPublishing || false;
      request = widgetsApi.updateRecord(remoteState.id || '', createUpdateWidgetPayload(config.widget), isPublishing);
    }

    return request.then(widgetUpdates => {
      handleSuccessUpsertResponse(widgetUpdates);
      return widgetUpdates;
    }).catch(err => {
      if (err instanceof BadRequestApiError) {
        throw errors.createBadWidgetSaveReqError(`${PREFIX} save: ${reqType}:`, err);
      } else if (err instanceof PaymentReqApiError) {
        setHasAcknowledgedInactiveSubscription(false);
        throw errors.createUnpaidWidgetSaveReqError(`${PREFIX} save: ${reqType}:`, err);
      } else if (err instanceof UnauthorizedApiError || err instanceof ForbiddenApiError) {
        throw errors.createUnauthWidgetSaveReqError(`${PREFIX} save: ${reqType}:`, err);
      } else {
        throw errors.createUnknownWidgetSaveReqError(`${PREFIX} save: ${reqType}:`, err);
      }
    }).finally(() => setIsLoading(false));
  }, [setIsLoading, handleSuccessUpsertResponse, setHasAcknowledgedInactiveSubscription, remoteState.id]); // Update a widget's reporting making
  // it a subscriber to another widget

  const updateReportingWidget = useCallback(selectedReportingWidgetId => {
    setIsLoading(true);
    return widgetsApi.updateReportingWidget(remoteState.id || '', selectedReportingWidgetId).catch(err => {
      if (err instanceof BadRequestApiError) {
        throw errors.createBadReporterReqError(`${PREFIX} updateReportingWidget:`, err);
      } else if (err instanceof ForbiddenApiError || err instanceof UnauthorizedApiError) {
        throw errors.createUnauthReporterReqError(`${PREFIX} updateReportingWidget:`, err);
      } else {
        throw errors.createUnknownReporterReqError(`${PREFIX} updateReportingWidget:`, err);
      }
    }).finally(() => setIsLoading(false));
  }, [setIsLoading, remoteState.id]); // Replace a widget's workbooks
  // making it a consumer of another workbook

  const replaceWorkbook = useCallback(async workbookId => {
    setIsLoading(true);
    const request = widgetsApi.updateRecord(remoteState.id || '', // @ts-ignore
    {
      workbook: workbookId
    }, false);
    return request.then(widgetUpdates => {
      handleSuccessUpsertResponse(widgetUpdates);
      return widgetUpdates;
    }).catch(err => {
      if (err instanceof BadRequestApiError) {
        throw errors.createBadWidgetSaveReqError(`${PREFIX} replaceWorkbook:`, err);
      } else if (err instanceof PaymentReqApiError) {
        throw errors.createUnpaidWidgetSaveReqError(`${PREFIX} replaceWorkbook:`, err);
      } else if (err instanceof UnauthorizedApiError || err instanceof ForbiddenApiError) {
        throw errors.createUnauthWidgetSaveReqError(`${PREFIX} replaceWorkbook:`, err);
      } else {
        throw errors.createUnknownWidgetSaveReqError(`${PREFIX} replaceWorkbook:`, err);
      }
    }).finally(() => setIsLoading(false));
  }, [setIsLoading, remoteState.id, handleSuccessUpsertResponse]); // Fully replace a widget with a new one
  // used for creating a new widget from a template

  const replaceWidget = useCallback(widget => {
    const clonedWidget = cloneEditableWidget(widget);
    setPayload(clonedWidget);
    setFormFieldsets(countWidgetFieldsets(clonedWidget));
    setUpdatedAt(clonedWidget.updatedAt);
    setHasUpdates(clonedWidget.updatedAt > 1);
  }, []); // Update top-level details of a widget
  // NOTE: only for simple updates (ie widget.title)

  const handleSimpleUpdate = useCallback(updates => {
    setPayload(_objectSpread(_objectSpread({}, payload), updates));
    setUpdatedAt(nowUnix);
    setHasUpdates(true);
  }, [payload, setPayload, setUpdatedAt, setHasUpdates]);
  const handleFontsChange = useCallback(fonts => {
    const updates = _objectSpread(_objectSpread({}, payload), {}, {
      fonts
    });

    deleteNullValues(updates);
    setPayload(updates);
    setUpdatedAt(nowUnix);
    setHasUpdates(true);
  }, [payload, setPayload, setUpdatedAt, setHasUpdates]);
  return {
    payload,
    updatedAt,
    isLoading,
    hasUpdates,
    hasAcknowledgedInactiveSubscription,
    formFieldsets,
    handleChange,
    handleUpload,
    save,
    updateReportingWidget,
    replaceWorkbook,
    replaceWidget,
    handleSimpleUpdate,
    handleFontsChange,
    acknowledgeInactiveSubscription
  };
} // Clone widget containing only
// editable fields present
// TODO: whitelist only attributes
//       user can update w/ this hook?

function cloneEditableWidget(widget) {
  const clone = cloneDeep(widget); // @ts-ignore

  delete clone.team;
  delete clone.workbook;
  return clone;
} // Make a publishiable payload
// only containing editable attributes


function createUpdateWidgetPayload(widget) {
  const clone = pick(cloneDeep(widget), EDITABLE_ATTRS); // Cleanup sections

  Object.keys(clone.sections || {}).forEach(id => {
    const section = (clone.sections || {})[id];
    const config = section.config || {};
    if (section.id) delete section.id;
    if (config.spreadsheet) delete config.spreadsheet;
  });
  return clone;
} // Count areas of widgets that would require
// new fieldsets, used to rerender form


function countWidgetFieldsets(widget) {
  return Object.keys(widget.sections || {}).map(id => (widget.sections || {})[id]).reduce((acc, section) => {
    const config = section.config || {};
    acc += // @ts-ignore
    (config.seriesOptions || []).length + (config.questions || []).length;
    return acc;
  }, 0);
} // Convert widget into a publishable POST API payload


function createNewWidgetPayload(widget, workbookId, projectId) {
  const result = {
    config: {}
  };
  if (widget.elementId) result.elementId = widget.elementId;
  if (widget.title) result.title = widget.title;
  if (widget.description) result.description = widget.description;
  if (widget.sections) result.sections = widget.sections;
  if (widget.config) result.config = widget.config;
  if (widget.tags) result.tags = widget.tags;
  if (projectId) result.project = projectId;
  if (workbookId) result.workbook = workbookId;
  return result;
}

// Handle events that are a side effect
// of specific updates to a widget
function handleUpdateSideEffects(_ref) {
  let {
    widgetId,
    meta
  } = _ref;
  const addedNewSection = meta.find(_ref2 => {
    let {
      type
    } = _ref2;
    return type === 'sections.added';
  });
  const addedNewQuestion = meta.find(_ref3 => {
    let {
      type
    } = _ref3;
    return type === 'questions.added';
  });

  if (addedNewSection) {
    setTimeout(() => {
      // Open widget edit modal after successful section creation
      selectEditingArea({
        widgetId,
        sectionId: addedNewSection.value
      });
    });
  }

  if (addedNewQuestion && addedNewQuestion.parent) {
    // Open question form for a newly created question
    selectEditingArea({
      sectionId: addedNewQuestion.parent,
      sectionEditingArea: addedNewQuestion.value
    });
  }
}